import { Component, Mixins, Ref } from "vue-property-decorator";

import VModal from "@/components/VModal.vue";
import {
  Granularity,
  RAW_FORMAT_GRANULARITIES,
} from "@/components/Agencies/ReportBuilder/granularity";
import { templateExportParameters } from "@/components/Agencies/ReportBuilder/templateParameters";
import { ModalAction } from "@/components/modalActionTypes";
import { ReportBuilderTabKey } from "@/mixins/ReportBuilder/ReportBuilderMixin";
import TemplateBulkDeleteMixin from "@/mixins/ReportBuilder/TemplateBulkDeleteMixin";
import { ReportTemplate } from "@/models";
import ToastInvoker, { responseErrorMessage } from "@/utils/ToastInvoker";

type GranularityDecision =
  | { kind: "omit" }
  | { kind: "comparison" }
  | { kind: "direct"; granularity: string | null }
  | { kind: "choose"; options: Granularity[] };

type GenerateActionKey = "cancel" | "generate";
type PeriodConfirmActionKey = "choosePeriod" | "generate";

@Component
export default class GenerateFromTemplateMixin extends Mixins(
  TemplateBulkDeleteMixin
) {
  @Ref("useTemplateModal") readonly useTemplateModal?: VModal;
  @Ref("periodConfirmModal") readonly periodConfirmModal?: VModal;
  @Ref("comparisonWarningModal") readonly comparisonWarningModal?: VModal;
  @Ref("granularityChoiceModal") readonly granularityChoiceModal?: VModal;

  readonly rawFormatGranularities = RAW_FORMAT_GRANULARITIES;

  pendingGranularityTemplate: ReportTemplate | null = null;
  pendingGranularityOptions: Granularity[] = [];
  pendingTemplateGranularityLabel: string | null = null;
  chosenGranularity: string | null = null;

  get showPendingTemplateInfo(): boolean {
    return this.hasPendingTemplate && this.isSingleReportTab;
  }

  get pendingTemplateActionsVisible(): boolean {
    return this.hasPendingTemplate && !this.isAutomaticTab;
  }

  get generatePendingDisabled(): boolean {
    return !this.isTemplateTab && !this.isBroadcastersStepEnabled;
  }

  get useTemplateModalActions(): ModalAction[] {
    return [
      {
        key: "close",
        label: "Chiudi",
        variant: "secondary",
        run: () => this.closeUseTemplateModal(),
      },
    ];
  }

  closeUseTemplateModal() {
    this.useTemplateModal?.hide();
  }

  async onGenerateFromUseTemplate(template: ReportTemplate) {
    this.closeUseTemplateModal();
    await this.startTemplateGeneration(template);
  }

  private resolveTemplateGranularity(
    template: ReportTemplate
  ): GranularityDecision {
    const competitor = template.competitor;
    if (!competitor || competitor.temporalDetail !== true) {
      return { kind: "omit" };
    }

    const hasComparison = !!(
      this.parameters.compare && this.parameters.compare_starts_at
    );
    if (hasComparison) {
      return { kind: "comparison" };
    }

    const options = this.granularityOptionsForDays(
      this.getSelectedDays(),
      competitor.raw
    );
    const stored = competitor.granularity;

    if (stored && options.some((o) => o.id === stored)) {
      return { kind: "direct", granularity: stored };
    }
    if (options.length === 0) {
      return { kind: "direct", granularity: null };
    }
    return { kind: "choose", options };
  }

  protected async startTemplateGeneration(template: ReportTemplate) {
    const decision = this.resolveTemplateGranularity(template);
    switch (decision.kind) {
      case "omit":
        await this.runTemplateReport(template.id);
        break;
      case "direct":
        await this.runTemplateReport(template.id, decision.granularity);
        break;
      case "comparison":
        this.pendingGranularityTemplate = template;
        this.comparisonWarningModal?.show();
        break;
      case "choose": {
        this.pendingGranularityTemplate = template;
        const stored = template.competitor?.granularity;
        this.pendingTemplateGranularityLabel =
          this.rawFormatGranularities
            .find((g) => g.id === stored)
            ?.text?.replace(/[()]/g, "") ?? null;
        this.pendingGranularityOptions = decision.options;
        this.chosenGranularity = null;
        this.granularityChoiceModal?.show();
        break;
      }
    }
  }

  confirmComparisonWarning() {
    const template = this.pendingGranularityTemplate;
    this.comparisonWarningModal?.hide();
    if (template) this.runTemplateReport(template.id, null);
  }

  closeComparisonWarning() {
    this.comparisonWarningModal?.hide();
  }

  onComparisonWarningHidden() {
    this.pendingGranularityTemplate = null;
  }

  get comparisonWarningModalActions(): ModalAction<GenerateActionKey>[] {
    return [
      {
        key: "cancel",
        label: "Annulla",
        variant: "outline-secondary",
        run: () => this.closeComparisonWarning(),
      },
      {
        key: "generate",
        label: "Genera Report",
        variant: "primary",
        run: () => this.confirmComparisonWarning(),
      },
    ];
  }

  confirmGranularityChoice() {
    const template = this.pendingGranularityTemplate;
    const granularity = this.chosenGranularity;
    if (!granularity) return;
    this.granularityChoiceModal?.hide();
    if (template) this.runTemplateReport(template.id, granularity);
  }

  closeGranularityChoice() {
    this.granularityChoiceModal?.hide();
  }

  onGranularityChoiceHidden() {
    this.pendingGranularityTemplate = null;
    this.pendingGranularityOptions = [];
    this.pendingTemplateGranularityLabel = null;
    this.chosenGranularity = null;
  }

  get granularityChoiceModalActions(): ModalAction<GenerateActionKey>[] {
    return [
      {
        key: "cancel",
        label: "Annulla",
        variant: "outline-secondary",
        run: () => this.closeGranularityChoice(),
      },
      {
        key: "generate",
        label: "Genera Report",
        variant: "primary",
        disabled: !this.chosenGranularity,
        run: () => this.confirmGranularityChoice(),
      },
    ];
  }

  private async runTemplateReport(
    templateId: string,
    granularity?: string | null
  ) {
    if (this.generatingReport) return;

    this.generatingReport = true;
    try {
      await this.postExportWithTemplate(
        templateExportParameters(this.parameters, templateId, granularity)
      );
      await this.successModal?.dialog();
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
    } finally {
      this.generatingReport = false;
    }
  }

  onGeneratePendingReport() {
    if (!this.isTemplateTab) {
      this.generatePendingTemplateReport();
      return;
    }
    if (!this.isBroadcastersStepEnabled) {
      this.goToPeriodForPendingTemplate();
      return;
    }
    this.periodConfirmModal?.show();
  }

  confirmGenerateFromTemplate() {
    this.periodConfirmModal?.hide();
    this.generatePendingTemplateReport();
  }

  goToPeriodForPendingTemplate() {
    this.periodConfirmModal?.hide();
    this.activeTab = ReportBuilderTabKey.SingleReport;
    this.currentStep = "period";
  }

  get periodConfirmModalActions(): ModalAction<PeriodConfirmActionKey>[] {
    return [
      {
        key: "choosePeriod",
        label: "No, scegli il periodo",
        variant: "outline-secondary",
        run: () => this.goToPeriodForPendingTemplate(),
      },
      {
        key: "generate",
        label: "Sì, genera",
        variant: "primary",
        run: () => this.confirmGenerateFromTemplate(),
      },
    ];
  }

  async generatePendingTemplateReport() {
    if (this.pendingTemplateId === null || !this.isBroadcastersStepEnabled) {
      return;
    }
    if (this.generatingReport) return;

    if (this.pendingTemplate) {
      await this.startTemplateGeneration(this.pendingTemplate);
    } else {
      await this.runTemplateReport(this.pendingTemplateId);
    }
  }
}
